import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { useAlertsStore } from '../store/alerts';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { StatusBadge } from '../components/ui/Badge';
import {
  Target, Search, RotateCcw, DollarSign,
  Smartphone, Monitor, Tablet, Bot, HelpCircle,
} from 'lucide-react';
import { formatCurrency, formatRelativeTime, formatDateTime, getDeviceLabel } from '../lib/utils';
import { CountryFlag } from '../components/ui/CountryFlag';
import { SubIdLightning } from '../components/SubIdLightning';

/**
 * Color-coded badge for the network's payout model.
 *
 * Heuristics live in postback worker (derivePayoutModel). Here we just style.
 * If `payoutModel` is null, fall back to eventType (e.g. "sale", "lead") so old
 * conversions before this column existed still show something sensible.
 */
function PayoutModelBadge({ model, fallback }: { model: string | null; fallback?: string }) {
  const value = (model || fallback || '').toLowerCase();
  if (!value || value === 'other') {
    return (
      <span className="text-[10px] font-mono uppercase tracking-wider text-text-tertiary bg-bg-tertiary px-2 py-0.5 rounded border border-border-subtle">
        {fallback || '—'}
      </span>
    );
  }
  // Color map — chosen so each model has a distinct neon hue
  const styles: Record<string, string> = {
    cpa: 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
    sale: 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
    cps: 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
    cpl: 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5',
    lead: 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5',
    soi: 'border-neon-green/40 text-neon-green bg-neon-green/5',
    doi: 'border-neon-magenta/40 text-neon-magenta bg-neon-magenta/5',
    install: 'border-neon-purple/40 text-neon-purple bg-neon-purple/5',
    cpi: 'border-neon-purple/40 text-neon-purple bg-neon-purple/5',
    revshare: 'border-neon-orange/40 text-neon-orange bg-neon-orange/5',
    trial: 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5',
    cc_submit: 'border-neon-red/40 text-neon-red bg-neon-red/5',
  };
  const cls = styles[value] ?? 'border-border-subtle text-text-secondary bg-bg-tertiary';
  const label = value === 'cc_submit' ? 'CC' : value.toUpperCase();
  return (
    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}>
      {label}
    </span>
  );
}

function DeviceIcon({ device, className = '' }: { device: string | null | undefined; className?: string }) {
  const d = (device ?? '').toLowerCase();
  const iconClass = `inline-block ${className}`;
  if (d.includes('bot')) return <Bot size={12} className={`${iconClass} text-neon-orange`} />;
  if (d.includes('mobile')) return <Smartphone size={12} className={`${iconClass} text-neon-cyan`} />;
  if (d.includes('tablet')) return <Tablet size={12} className={`${iconClass} text-neon-purple`} />;
  if (d.includes('desktop')) return <Monitor size={12} className={`${iconClass} text-neon-green`} />;
  return <HelpCircle size={12} className={`${iconClass} text-text-tertiary`} />;
}
import { useAuth } from '../store/auth';
import { usePollMultiplier } from '../hooks/usePollMultiplier';
import { useOfferLookup } from '../hooks/useLookups';

// Match the rest of the dashboard's range vocabulary — "yesterday" is a
// common publisher need (verify daily payout numbers, compare to today),
// "90d" rarely used so removed in favor of explicit yesterday slot.
const ranges = ['today', 'yesterday', '7d', '30d', 'custom'] as const;
type Range = (typeof ranges)[number];

function utcDateInput(ts = Date.now()): string {
  return new Date(ts).toISOString().slice(0, 10);
}

interface Conversion {
  id: number;
  clickId: string;
  subId: string | null;
  offerId: number | null;
  country: string | null;
  deviceType: string | null;
  source: string | null;
  ipAddress: string | null;
  revenue: number;
  payout: number;
  status: string;
  eventType: string;
  networkOfferName: string | null;
  networkGoal: string | null;
  payoutModel: string | null;
  createdAt: number;
  approvedAt: number | null;
  postbackReceivedAt: number | null;
}

export function ConversionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const offerLookup = useOfferLookup();
  const pollMult = usePollMultiplier();
  // NOTE: reverted user-role auto-scoping per user request. Conversions
  // remains a network-wide view for both roles.
  const [range, setRange] = useState<Range>('today');
  const [search, setSearch] = useState('');

  // While on /conversions, the global ConversionAlertProvider skips its
  // own polling (smart-skip — no point fetching same data twice). To avoid
  // a thundering-herd of toasts when user navigates AWAY from this page,
  // we update the alert provider's high-water mark (lastSeenConversionId)
  // as new entries appear here. The alert provider resumes polling from
  // that mark, so it only toasts entries that arrive AFTER user leaves.
  const setLastSeenId = useAlertsStore((s) => s.setLastSeenConversionId);
  const [customFrom, setCustomFrom] = useState(() => utcDateInput());
  const [customTo, setCustomTo] = useState(() => utcDateInput());
  const customValid = customFrom <= customTo;
  const rangeParams =
    range === 'custom'
      ? { range, from: customFrom, to: customTo }
      : { range };
  const rangeKey =
    range === 'custom' ? `${range}:${customFrom}:${customTo}` : range;

  // Limit scaled to range. Today: 50 rows is plenty for live monitoring
  // (search filter exists for drilling down). Longer ranges keep bigger
  // default for historical review. Cuts payload ~10x for the most
  // frequently-polled view.
  const conversionsLimit = range === 'today' ? 50 : 500;
  const list = useQuery({
    queryKey: ['conversions', rangeKey],
    queryFn: () =>
      apiClient.get<Conversion[]>('/api/v1/conversions', {
        // Use range param (server resolves via resolveRange) so the table
        // matches the KPI overview window for the same range — especially
        // important for "yesterday" which needs an upper-bound, not just
        // since_days backward-looking.
        ...rangeParams,
        limit: conversionsLimit,
      }),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // BUGFIX: previously KPI counts/sums were computed from `list.data` — but
  // list is capped at 50 rows for today (or 500 for longer ranges). So the
  // header showed "50 conversions, $160" when the DB actually had 157 / $450.
  // User caught this comparing with Statistics page: "Statistics dan
  // conversions kenapa berbeda?". Fix: pull totals from the same stats
  // overview endpoint Statistics uses. Tiny payload, server-side cached
  // (60s KV), no row-data dependency.
  const overview = useQuery({
    queryKey: ['conversions-kpi-overview', rangeKey],
    queryFn: () =>
      apiClient.get<{
        conversionsApproved: number;
        revenueGross: number;
        payoutGross: number;
      }>('/api/v1/stats/overview', rangeParams),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // Sync max conversion id seen → alert provider's high-water mark, so when
  // user navigates away the global alert poller resumes from this point
  // (no duplicate toasts for entries already visible in this table).
  useEffect(() => {
    const rows = list.data;
    if (!rows || rows.length === 0) return;
    const maxId = Math.max(...rows.map((r) => r.id));
    if (maxId > 0) setLastSeenId(maxId);
  }, [list.data, setLastSeenId]);

  const filtered = useMemo(() => {
    let rows = list.data ?? [];
    rows = rows.filter((r) => r.status !== 'reversed' && r.status !== 'rejected');
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => {
        const offerName = offerLookup.getName(r.offerId);
        return (
          (r.subId ?? '').toLowerCase().includes(q) ||
          (r.clickId ?? '').toLowerCase().includes(q) ||
          (r.country ?? '').toLowerCase().includes(q) ||
          (r.source ?? '').toLowerCase().includes(q) ||
          (r.ipAddress ?? '').toLowerCase().includes(q) ||
          offerName.toLowerCase().includes(q) ||
          (r.networkOfferName ?? '').toLowerCase().includes(q) ||
          (r.networkGoal ?? '').toLowerCase().includes(q) ||
          (r.payoutModel ?? '').toLowerCase().includes(q)
        );
      });
    }
    return rows;
  }, [list.data, search, offerLookup]);

  const visibleTotals = useMemo(() => ({
    revenue: filtered.reduce((sum, row) => sum + (row.revenue ?? 0), 0),
  }), [filtered]);

  // Quick stats. Counts + sums come from the overview endpoint (full
  // aggregate, not capped at the table's 50-row fetch). Reversed counts
  // are derived from the visible rows because there's no dedicated
  // reversed aggregate yet — minor edge case (reversed conversions are
  // rare, usually 0). If reversed becomes operationally important, add
  // a `reversedCount` field server-side in overview.
  const stats = useMemo(() => {
    const ov = overview.data;
    const rows = list.data ?? [];
    const reversed = rows.filter((r) => r.status === 'reversed' || r.status === 'rejected');
    return {
      count: ov?.conversionsApproved ?? 0,
      revenue: ov?.revenueGross ?? 0,
      payout: ov?.payoutGross ?? 0,
      reversedCount: reversed.length,
      reversedRevenue: reversed.reduce((s, r) => s + (r.revenue ?? 0), 0),
    };
  }, [overview.data, list.data]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
            <Target size={20} className="text-neon-magenta" />
            {isAdmin ? 'Conversions' : 'My Conversions'}
          </h1>
          <p className="text-sm text-text-secondary font-mono">
            // postbacks dari iMonetizeIt / Trafee / network lain · {filtered.length} records ·{' '}
            {range === 'custom' ? `${customFrom}..${customTo} UTC` : range}
          </p>
        </div>
        <div className="flex flex-col sm:items-end gap-2">
          <div className="inline-flex bg-bg-secondary border border-border-subtle rounded-sm p-1">
            {ranges.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm transition-all ${
                  range === r ? 'bg-neon-magenta/10 text-neon-magenta' : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          {range === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="input-cyber h-9 w-36"
              />
              <span className="text-text-tertiary">to</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="input-cyber h-9 w-36"
              />
              {!customValid && (
                <span className="text-neon-red">from must be before to</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="!p-4">
          <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1">Conversions</div>
          <div className="text-2xl font-display font-bold text-neon-magenta numeric">{stats.count}</div>
        </Card>
        <Card className="!p-4">
          <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1 flex items-center gap-1">
            <DollarSign size={10} /> Revenue
          </div>
          <div className="text-2xl font-display font-bold text-neon-yellow numeric">{formatCurrency(stats.revenue)}</div>
        </Card>
        <Card className="!p-4">
          <div className="text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1 flex items-center gap-1">
            <RotateCcw size={10} /> Reversed
          </div>
          <div className="text-2xl font-display font-bold text-neon-red numeric">{stats.reversedCount}</div>
          {stats.reversedCount > 0 && (
            <div className="text-[10px] text-text-tertiary font-mono mt-1">−{formatCurrency(stats.reversedRevenue)}</div>
          )}
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1 max-w-xs">
          <Input
            placeholder="Search Sub ID / Country / Source / IP / Offer / Goal..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={<Search size={14} />}
          />
        </div>
      </div>

      {/* Table */}
      <Card className="p-0">
        <div className="px-5 py-4 border-b border-border-subtle">
          <h3 className="font-display text-sm uppercase tracking-wider text-neon-magenta flex items-center gap-2">
            <Target size={14} />
            Conversion Stream
            {list.isRefetching && (
              <span className="ml-2 text-[10px] text-text-tertiary font-mono">syncing...</span>
            )}
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Time</th>
                <th>Sub ID</th>
                <th>Country</th>
                <th>Device</th>
                <th>Source</th>
                <th>IP</th>
                <th>Offer</th>
                <th>Goal</th>
                <th className="text-right">Revenue</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className={c.status === 'reversed' || c.status === 'rejected' ? 'opacity-50' : ''}>
                  <td className="font-mono text-xs text-text-secondary whitespace-nowrap" title={formatDateTime(c.createdAt)}>
                    {formatRelativeTime(c.createdAt)}
                  </td>
                  <td className="font-mono text-xs text-neon-cyan">
                    {c.subId
                      ? <SubIdLightning subId={c.subId}>{c.subId}</SubIdLightning>
                      : <span className="text-text-tertiary">—</span>}
                  </td>
                  <td>
                    {c.country ? <CountryFlag iso2={c.country} showCode size={20} /> : '—'}
                  </td>
                  <td>
                    {c.deviceType ? (
                      <span className="flex items-center gap-1.5 font-mono text-xs">
                        <DeviceIcon device={c.deviceType} />
                        <span>{getDeviceLabel(c.deviceType)}</span>
                      </span>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                  <td className="font-mono text-xs text-text-secondary">
                    {c.source ? (
                      <span className="text-neon-cyan/80">{c.source}</span>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                  <td className="font-mono text-xs text-text-secondary whitespace-nowrap">
                    {c.ipAddress ? (
                      <span title={c.ipAddress}>{c.ipAddress}</span>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-col leading-tight">
                      <span
                        className="font-mono text-xs text-neon-cyan"
                        title={`Offer ID: ${c.offerId ?? 'unlinked'}`}
                      >
                        {offerLookup.getName(c.offerId)}
                      </span>
                      {c.networkOfferName && (
                        <span
                          className="text-[10px] font-mono text-text-tertiary truncate max-w-[200px]"
                          title={c.networkOfferName}
                        >
                          {c.networkOfferName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <PayoutModelBadge model={c.payoutModel} fallback={c.eventType} />
                      {c.networkGoal && c.networkGoal.toLowerCase() !== (c.payoutModel ?? '') && (
                        <span
                          className="text-[9px] font-mono text-text-tertiary"
                          title={`Network goal: ${c.networkGoal}`}
                        >
                          {c.networkGoal}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-right numeric text-neon-yellow">{formatCurrency(c.revenue)}</td>
                  <td><StatusBadge status={c.status} /></td>
                </tr>
              ))}
              {filtered.length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={11} className="text-center py-14 text-text-tertiary">
                    <Target className="mx-auto mb-3 opacity-30" size={36} />
                    <p className="font-mono text-xs">
                      {search ? 'no results matching your filter' : 'no conversions yet — waiting for postbacks from networks'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-border-subtle bg-bg-tertiary/40">
                  <td
                    colSpan={8}
                    className="font-display text-xs uppercase tracking-wider text-text-secondary py-3"
                  >
                    TOTAL
                    {search
                      ? ` · ${filtered.length} of ${list.data?.length ?? 0} (filtered)`
                      : ` · ${filtered.length} rows`}
                  </td>
                  <td className="text-right numeric font-display text-sm font-bold text-neon-yellow py-3">
                    {formatCurrency(visibleTotals.revenue)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}
